import * as yup from 'yup';
import { StringSchema } from 'yup';
import { BaseFormField, BaseFormFieldTypes, FormField } from '@/type';

const REQUIRED_VALIDATION_NAME = 'required';
const REQUIRED_MULTI_SELECT_VALIDATION_NAME = 'CUSTOM_REQUIRED_MULTI_SELECT_VALIDATION';

export const REQUIRED_TEST_NAMES = [
  REQUIRED_VALIDATION_NAME,
  REQUIRED_MULTI_SELECT_VALIDATION_NAME,
];

export function buildFormFieldLabel(label: string, schema: StringSchema): string {
  const hasRequiredTest = schema?.tests.some((test) => {
    return REQUIRED_TEST_NAMES.includes(test?.OPTIONS?.name);
  });
  return `${label}${hasRequiredTest ? ' *' : ''}`;
}

export const buildValidationSchemaFromFormFields = (formFields: FormField[]) => {
  const validationFields: Record<string, yup.AnySchema> = {};

  formFields.forEach((field) => {
    if (isFieldWithValidation(field)) {
      const { name } = field;
      validationFields[name] = field.validation;
    }
  });

  return yup.object().shape(validationFields);
};

export function isFieldWithValidation(field: FormField): field is BaseFormFieldTypes {
  return !!field.validation && !!field.name && 'name' in field && 'label' in field;
}

export const buildValidationSchema = (formFields: FormField[]): yup.AnySchema => {
  const staticFields = new Set<FormField>();
  const nestedFieldsMap = new Map<string, Set<FormField>>();

  processFormFields(formFields, staticFields, nestedFieldsMap);

  const staticValidationSchema = buildValidationSchemaFromFormFields(
    Array.from(staticFields),
  );

  const nestedValidationSchemas = buildNestedValidationSchemas(nestedFieldsMap);

  return yup.object().shape({
    ...staticValidationSchema.fields,
    ...nestedValidationSchemas,
  });
};

const processFormFields = (
  formFields: FormField[],
  staticFields: Set<FormField>,
  nestedFieldsMap: Map<string, Set<FormField>>,
) => {
  formFields
    ?.filter((formField) => isFieldWithValidation(formField))
    .forEach((formField) => {
      const { name } = formField as BaseFormField;
      if (name.includes('.')) {
        handleNestedField(formField, nestedFieldsMap);
      } else {
        staticFields.add(formField);
      }
    });
};

const handleNestedField = (
  formField: FormField,
  nestedFieldsMap: Map<string, Set<FormField>>,
) => {
  if ('name' in formField) {
    const [parentName, childName] = formField.name.split('.');
    if (!nestedFieldsMap.has(parentName)) {
      nestedFieldsMap.set(parentName, new Set<FormField>());
    }
    const nestedFields = nestedFieldsMap.get(parentName);
    nestedFields?.add({ ...formField, name: childName } as FormField);
  }
};

const buildNestedValidationSchemas = (
  nestedFieldsMap: Map<string, Set<FormField>>,
): { [key: string]: yup.AnySchema } => {
  const nestedValidationSchemas = {};
  nestedFieldsMap.forEach((fields, parentName) => {
    nestedValidationSchemas[parentName] = buildValidationSchemaFromFormFields(
      Array.from(fields),
    );
  });
  return nestedValidationSchemas;
};
